import { AlertCircle, X } from "lucide-react";
import { motion } from "framer-motion";

const problems = [
  "Not showing up on Google in the suburbs you want",
  "Competitors look more professional online than you",
  "Your website isn't converting visitors into enquiries",
  "Not enough 5-star Google reviews to build trust",
  "Missed calls mean missed jobs every week",
  "Slow follow-up losing you quotes to faster competitors",
  "No visibility on what marketing is actually working",
  "Marketing agencies that don't understand the trades industry",
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

export function ProblemSection() {
  return (
    <section id="problem" className="section-padding bg-background relative overflow-hidden" aria-labelledby="problem-heading">
      {/* Subtle background accent */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-primary/[0.02] rounded-full blur-[80px] sm:blur-[100px] translate-x-1/2 -translate-y-1/2" />
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-6 sm:mb-8"
        >
          <h2 id="problem-heading" className="heading-section text-foreground text-balance max-w-[800px] mx-auto">
            Why Quality Tradies Lose Jobs to Competitors With{" "}
            <span className="text-primary">Weaker Workmanship</span>
          </h2>
        </motion.div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-4 lg:gap-6 items-stretch max-w-[1000px] mx-auto">
          {/* Left Column */}
          <motion.div 
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col"
          >
            <div className="space-y-2 mb-4 text-center lg:text-left">
              <p className="text-base sm:text-lg text-foreground font-semibold">You're great at what you do — quality workmanship, every time.</p>
              <p className="text-muted-foreground text-[0.9375rem] sm:text-base leading-[1.7]">You deliver excellent results for your customers.</p>
              <p className="text-muted-foreground text-[0.9375rem] sm:text-base leading-[1.7]">You run a professional trade or construction business.</p>
            </div>

            <div className="bg-card rounded-xl border border-border/50 p-4 sm:p-5 flex-1 shadow-card">
              <div className="flex items-center gap-2.5 mb-3 justify-center lg:justify-start">
                <div className="w-8 h-8 rounded-lg bg-warning/15 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-warning" />
                </div>
                <h3 className="text-foreground font-bold text-[0.9375rem] sm:text-base lg:text-lg">
                  The truth most trade businesses never hear:
                </h3>
              </div>
              <p className="text-muted-foreground leading-[1.7] text-[0.9375rem] sm:text-base text-center lg:text-left">
                Customers decide who to call{" "}
                <strong className="text-foreground font-semibold">before they ever speak to you</strong>{" "}
                — your Google presence, website, and reviews determine whether you get the enquiry or your competitor does.
              </p>
            </div>
          </motion.div>

          {/* Right Column - Problems List */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="bg-card rounded-xl border border-border/50 p-4 sm:p-5 shadow-card"
          >
            <h3 className="text-[0.9375rem] sm:text-base lg:text-lg font-bold text-foreground mb-3 text-center lg:text-left">
              Common digital marketing problems for trade businesses:
            </h3>
            <motion.ul 
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              className="space-y-2"
              aria-label="List of common problems tradies face with digital marketing"
            >
              {problems.map((problem, index) => (
                <motion.li
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-2.5 text-muted-foreground group"
                >
                  <div className="w-5 h-5 rounded-full bg-[#D9534F]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3 h-3 text-[#D9534F]" strokeWidth={2.5} />
                  </div>
                  <span className="leading-[1.65] text-[0.9375rem] sm:text-base">{problem}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </div>

        {/* Transition line */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 sm:mt-8 text-center"
        >
          <div className="inline-block bg-card rounded-xl border border-primary/15 px-5 sm:px-8 py-4 sm:py-5 shadow-card">
            <p className="text-[0.9375rem] sm:text-base text-muted-foreground leading-[1.65] mb-1">
              These aren't skill problems.
            </p>
            <p className="text-base sm:text-lg font-semibold text-foreground">
              They're digital marketing problems — and they can be fixed with the right <a href="#system-section" className="text-primary hover:underline">growth system</a>.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
